import React, { useState, memo } from 'react';

const FamousPlaces = memo(({ data }) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [imageError, setImageError] = useState(false);

  if (!data) return null;

  const handleImageLoad = () => {
    setImageLoaded(true);
  };

  const handleImageError = () => {
    setImageError(true);
    setImageLoaded(true);
  };

  return (
    <article 
      className="group w-full h-full "
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="bg-white rounded-xl overflow-hidden shadow-[rgba(0,0,0,0.08)_0px_4px_20px_0px] hover:shadow-[rgba(0,0,0,0.25)_0px_20px_40px_0px] transition-all duration-500 ease-out transform sm:hover:scale-[1.02] sm:hover:-translate-y-2 h-full flex flex-col">
        
        {/* Enhanced Image Section */}
        <div className="relative aspect-[4/3] w-full overflow-hidden">
          {!imageError ? (
            <>
              <img
                src={data.mainImageUrl}
                alt={`Beautiful view of ${data.placeName}`}
                className={`w-full h-full object-cover transition-all duration-700 ease-out ${
                  isHovered ? 'sm:scale-110 sm:brightness-110' : 'scale-100 brightness-100'
                }`}
                onLoad={handleImageLoad}
                onError={handleImageError}
                loading="lazy"
              />
              
              {/* Loading shimmer */}
              {!imageLoaded && (
                <div className="absolute inset-0 bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200 bg-[length:200%_100%] animate-[shimmer_1.5s_ease-in-out_infinite]">
                  <div className="absolute inset-0 bg-gray-200/50"></div>
                </div>
              )}
            </>
          ) : (
            // Fallback for missing images
            <div className="w-full h-full bg-gradient-to-br from-[#10c9c0]/20 to-[#0eb1a9]/20 flex items-center justify-center">
              <svg className="w-16 h-16 text-[#10c9c0]/60" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </div>
          )}
          
          {/* Gradient overlay on hover */}
          <div className={`absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent transition-all duration-500 ${
            isHovered ? 'opacity-100' : 'opacity-0'
          }`}></div>
          
          {/* Floating badge */}
          <div className={`absolute top-3 left-3 bg-white/90 backdrop-blur-sm text-[#10c9c0] px-3 py-1.5 rounded-full text-sm font-semibold shadow-lg transition-all duration-500 ${
            isHovered ? 'scale-105 bg-white' : 'scale-100'
          }`}>
            Famous Place
          </div>
        </div>

        {/* Enhanced Content Section */}
        <div className="flex-1 flex flex-col p-4 sm:p-6 space-y-3 sm:space-y-4">
          
          {/* Title with responsive sizing */}
          <h2 className={`text-[#10c9c0] font-bold text-lg sm:text-xl md:text-2xl leading-tight line-clamp-2 transition-all duration-500 ${
            isHovered ? 'text-[#0eb1a9] transform translate-y-[-1px]' : 'transform translate-y-0'
          }`}>
            {data.placeName}
          </h2>
          
          {/* Description with better typography */}
          <p className={`text-[#949995] text-sm sm:text-base leading-relaxed flex-1 line-clamp-4 sm:line-clamp-5 transition-all duration-500 delay-75 ${
            isHovered ? 'text-[#737873]' : 'text-[#949995]'
          }`}>
            {data.aboutThePlace}
          </p>
          

        </div>
        
        {/* Subtle accent border */}
        <div className={`absolute inset-0 rounded-xl border-2 border-[#10c9c0] transition-all duration-500 pointer-events-none ${
          isHovered ? 'border-opacity-30' : 'border-transparent'
        }`}></div>
        
        {/* Bottom accent line */}
        <div className={`absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-[#10c9c0] to-[#0eb1a9] transition-all duration-500 ${
          isHovered ? 'opacity-100 scale-x-100' : 'opacity-0 scale-x-0'
        } origin-center`}></div>
      </div>
      
      {/* Shimmer animation */}
      <style jsx>{`
        @keyframes shimmer {
          0% {
            background-position: -200% 0;
          }
          100% {
            background-position: 200% 0;
          }
        }
      `}</style>
    </article>
  );
});

FamousPlaces.displayName = 'FamousPlaces';

export default FamousPlaces;